import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { getTaskList } from '@/service/ManhattanService';
import { getNotifications } from '@/service/notificationService';
import { useAppSelector } from '@/store';
import { mapNotificationDTO, getLastNotificationTime } from '@/utils';

import type { NotificationFilterType } from '@/constants';
import type { TaskDetail, NotificationModel } from '@/types';

// ─────────────────────────────────────────────────────────────────
// Query Keys
// ─────────────────────────────────────────────────────────────────

export const dashboardQueryKeys = {
  all: ['dashboard'] as const,
  upcomingTasks: (orgId: string) => ['dashboard', 'upcoming', orgId] as const,
  myTasks: (orgId: string, userId: string) => ['dashboard', 'myTasks', orgId, userId] as const,
  completedTasks: (orgId: string, userId: string) =>
    ['dashboard', 'completed', orgId, userId] as const,
  inProgressTask: (orgId: string, userId: string) =>
    ['dashboard', 'inProgress', orgId, userId] as const,
  notificationCount: (orgId: string) => ['dashboard', 'notifications', orgId] as const,
  notificationList: (orgId: string, filterBy: string) =>
    ['dashboard', 'notificationList', orgId, filterBy] as const,
};

// ─────────────────────────────────────────────────────────────────
// Query Hooks
// ─────────────────────────────────────────────────────────────────

export const useUpcomingTasksQuery = () => {
  const orgId = useAppSelector((state) => String(state.user.plant?.org_id ?? ''));

  return useQuery({
    queryKey: dashboardQueryKeys.upcomingTasks(orgId),
    queryFn: (): Promise<TaskDetail[]> => getTaskList('upcoming', orgId),
    enabled: !!orgId,
    staleTime: 0,
    refetchInterval: 60 * 1000,
  });
};

export const useMyTasksQuery = () => {
  const orgId = useAppSelector((state) => String(state.user.plant?.org_id ?? ''));
  // Matches Xamarin: UserId = countryCode + mobileNo (used in OIC ASSIGNED_USER_ID filter)
  const userId = useAppSelector(
    (state) => `${state.user.user?.country_code ?? ''}${state.user.user?.mobile_no ?? ''}`
  );

  return useQuery({
    queryKey: dashboardQueryKeys.myTasks(orgId, userId),
    networkMode: 'always',
    queryFn: (): Promise<TaskDetail[]> => getTaskList('myTasks', orgId, userId),
    enabled: !!orgId && !!userId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useCompletedTasksQuery = () => {
  const orgId = useAppSelector((state) => String(state.user.plant?.org_id ?? ''));
  // Matches Xamarin: UserId = countryCode + mobileNo (used in OIC ASSIGNED_USER_ID filter)
  const userId = useAppSelector(
    (state) => `${state.user.user?.country_code ?? ''}${state.user.user?.mobile_no ?? ''}`
  );

  return useQuery({
    queryKey: dashboardQueryKeys.completedTasks(orgId, userId),
    networkMode: 'always',
    queryFn: (): Promise<TaskDetail[]> => getTaskList('completed', orgId, userId),
    enabled: !!orgId && !!userId,
    staleTime: 0,
    refetchInterval: 60 * 1000,
  });
};

export const useInProgressTaskQuery = () => {
  const orgId = useAppSelector((state) => String(state.user.plant?.org_id ?? ''));
  // Matches Xamarin: UserId = countryCode + mobileNo (used in OIC ASSIGNED_USER_ID filter)
  const userId = useAppSelector(
    (state) => `${state.user.user?.country_code ?? ''}${state.user.user?.mobile_no ?? ''}`
  );

  return useQuery({
    queryKey: dashboardQueryKeys.inProgressTask(orgId, userId),
    networkMode: 'always',
    queryFn: async (): Promise<TaskDetail | null> => {
      const tasks = await getTaskList('inProgress', orgId, userId);
      return tasks[0] ?? null;
    },
    enabled: !!orgId && !!userId,
    staleTime: 30 * 1000,
  });
};

// Matches Xamarin: uses MiddlewareFunctionCodes.Notifications (not FunctionCodes.notifications)
// and Plant.OrgId (not Plant._id) as the plant_id parameter
export const useNotificationCountQuery = () => {
  const orgId = useAppSelector((state) => String(state.user.plant?.org_id ?? ''));

  return useQuery({
    queryKey: dashboardQueryKeys.notificationCount(orgId),
    queryFn: async (): Promise<number> => {
      try {
        const notifications = await getNotifications(50, 'All');
        if (!Array.isArray(notifications)) return 0;
        const lastTime = getLastNotificationTime();
        const lastDate = lastTime ? new Date(lastTime).getTime() : 0;
        const seenTaskIds = new Set<string>();
        for (const n of notifications) {
          if (lastTime && new Date(n.updatedAt).getTime() <= lastDate) continue;
          const key = n.task_id != null ? String(n.task_id) : n._id;
          seenTaskIds.add(key);
        }
        return seenTaskIds.size;
      } catch {
        return 0;
      }
    },
    enabled: !!orgId,
    staleTime: 2 * 60 * 1000,
  });
};

// Matches Xamarin NotificationListViewModel.ShowDefaultNotifications / ShowAllNotifications
export const useNotificationsQuery = (filterBy: NotificationFilterType, enabled: boolean) => {
  const orgId = useAppSelector((state) => String(state.user.plant?.org_id ?? ''));
  const { t } = useTranslation();

  return useQuery({
    queryKey: dashboardQueryKeys.notificationList(orgId, filterBy),
    queryFn: async (): Promise<NotificationModel[]> => {
      const dtos = await getNotifications(50, filterBy);
      if (!Array.isArray(dtos)) return [];
      const lastTime = getLastNotificationTime();
      return dtos.map((dto) => mapNotificationDTO(dto, lastTime, t));
    },
    enabled: enabled && !!orgId,
    staleTime: 2 * 60 * 1000,
  });
};
